import { CalendarClock, ClipboardCheck, ListChecks, Paperclip, RotateCcw, UserRound } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../../../auth/AuthContext';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ErrorState } from '../../../../components/ErrorState';
import type { FolderTab } from '../../../../components/FolderTabs';
import type { MenuItem } from '../../../../components/Menu';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { PreviewGrid } from '../../../../components/PreviewGrid';
import { PreviewTable, type PreviewRow } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell, ProfileHeaderSkeleton } from '../../../../components/profile/ProfileHeaderShell';
import { ProfileNav } from '../../../../components/profile/ProfileNav';
import { Skeleton } from '../../../../components/Skeleton';
import { StatusText, type StatusTone } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { Tooltip } from '../../../../components/Tooltip';
import { ApiError } from '../../../../lib/apiError';
import { formatClockTime, formatDateOnly, formatDateTime } from '../../../../lib/formatDate';
import { saveBlob } from '../../../../lib/saveBlob';
import { stageOutcome } from '../../deliberation/stageOutcome';
import { usePersonNames } from '../../personnel/usePersonNames';
import { applicantDisplayName } from '../applicantName';
import { applicantsApi } from '../applicantsApi';
import {
  APPLICANT_ASSESSMENT_STAGES,
  APPLICANT_HEALTH_PERMISSION,
  APPLICANT_QUALIFICATION,
  APPLICANT_STATUS,
  type ApplicantAssessment,
  type ApplicantChecklistItem,
  type ApplicantListItem,
  type ApplicantScoreWeights,
  type JobPostingListItem,
} from '../applicantTypes';
import { ApplicantAssessmentModal } from '../components/ApplicantAssessmentModal';
import { ApplicantAssessmentPanel } from '../components/ApplicantAssessmentPanel';
import { ApplicantSummaryPanel } from '../components/ApplicantSummaryPanel';
import { ApplicantInterviewModal } from '../components/ApplicantInterviewModal';
import { ApplicantModal } from '../components/ApplicantModal';
import { ApplicantScreeningModal, type ScreeningStepKey } from '../components/ApplicantScreeningModal';
import { ApplicantStatusModal } from '../components/ApplicantStatusModal';

type TabKey = 'info' | 'initial-screening' | 'initial-interview' | 'assessments';

type AssessmentTabKey = (typeof APPLICANT_ASSESSMENT_STAGES)[number]['key'];

/** One entry of the Assessments tab's rail — a stage, or the Summary. */
type AssessmentPanelKey = AssessmentTabKey | 'summary';

/**
 * One tab per stage that RECORDS something before the assessments, in the cycle's order, then
 * **Assessments** (product call, 21 Oct 2026): the five stages and the Summary moved off the strip into
 * ONE tab, picked from a vertical rail inside it — Deliberate's rail, P / F / - badges and all. The
 * statuses (For Assessment, For Deliberation, Selected, Not Selected) are the header pill.
 */
const TABS: FolderTab<TabKey>[] = [
  { key: 'info', label: 'Info' },
  { key: 'initial-screening', label: 'Initial Screening' },
  { key: 'initial-interview', label: 'Initial Interview' },
  { key: 'assessments', label: 'Assessments' },
];

/**
 * The Assessments tab's rail: the five stages in order, each stating its outcome at the outer right, then
 * the Summary. **A HEALTH stage carries `applicants.read-medical`** — gating HIDES it, and the server
 * leaves its values out without the key, so the entry and its content share one gate. No group header:
 * the TAB already says Assessments, and the rail would only print it twice.
 */
function assessmentEntries(assessments: ApplicantAssessment[] | null) {
  return [
    ...APPLICANT_ASSESSMENT_STAGES.map((s) => ({
      key: s.key,
      label: s.label,
      permission: s.isHealth ? APPLICANT_HEALTH_PERMISSION : undefined,
      badge: assessments ? stageOutcome(assessments.find((a) => a.stage === s.value)) : undefined,
    })),
    { key: 'summary', label: 'Summary' },
  ];
}

/**
 * One applicant, on the **header** layout.
 *
 * **The banner owns the lifecycle** — the application's stage, with the NEXT process for where the
 * applicant stands: **Initial screening**, then **Schedule interview**, then **Change status** (each
 * opens a form, so the form's own tick is the gate); once deleted, **Restore** (a write, so it
 * confirms). **The header cog lists all three processes always** (19 Oct 2026: "allow the user to do
 * the other processes" here), a stage not open yet DISABLED with its reason. The Applicant card's Edit
 * corrects the name and contact; Delete is last in the header cog.
 *
 * **The vacancy's cycle has two stages on the applicant** (19 Oct 2026), each its own TAB: **Initial
 * Screening** (which SETS Qualified / Not Qualified) — a card per step of its form, the Recruitment
 * checklist as a grid and the Screening as a field table — and **Initial Interview** (a Qualified
 * applicant only). An unrecorded stage shows its empty state and the way to fill it. `Info` keeps the
 * applicant and the Audit card, and is where the page lands.
 *
 * **Then the five ASSESSMENT stages** (20 Oct 2026) — Final Interview, Written Exam, Neuro-Psychiatric,
 * Medical, Background Interview — each a tab of one card (Result, Remarks, Attachments) over its own
 * form; **none gates another**, and each is open to a Qualified applicant only. The two health stages
 * are tabs only for `applicants.read-medical`. **Summary** scores them against Score Points.
 *
 * The NAME is resolved through the applicant-scoped door, and the edit form reads its parts only
 * when opened — that read is audited.
 */
export function ApplicantProfilePage() {
  const { postingId = '', applicantId = '' } = useParams<{ postingId: string; applicantId: string }>();
  const toast = useToast();
  const { hasPermission } = useAuth();

  const [applicant, setApplicant] = useState<ApplicantListItem | null>(null);
  const [posting, setPosting] = useState<JobPostingListItem | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [tab, setTab] = useState<TabKey>('info');
  /** The Assessments tab's open entry — it lands on the Summary, as Deliberate's rail does. */
  const [assessmentPanel, setAssessmentPanel] = useState<AssessmentPanelKey>('summary');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [movingStatus, setMovingStatus] = useState(false);
  /** The screening form, and the step it opens on — null while closed. */
  const [screening, setScreening] = useState<ScreeningStepKey | null>(null);
  const [interviewing, setInterviewing] = useState(false);
  /** The checklist as recorded — the Recruitment checklist card's rows. */
  const [checklist, setChecklist] = useState<ApplicantChecklistItem[] | null>(null);
  const [checklistFailed, setChecklistFailed] = useState(false);
  /** Every assessment stage, recorded or not, and the Score Points that weight the Summary. */
  const [assessments, setAssessments] = useState<ApplicantAssessment[] | null>(null);
  const [weights, setWeights] = useState<ApplicantScoreWeights | null>(null);
  const [assessmentsFailed, setAssessmentsFailed] = useState(false);
  /** The stage whose form is open — null while closed. */
  const [recording, setRecording] = useState<ApplicantAssessment | null>(null);

  /** Which confirmation is open — one piece of state, so two dialogs can never stack. */
  const [confirming, setConfirming] = useState<'delete' | 'restore' | null>(null);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([applicantsApi.get(applicantId, controller.signal), applicantsApi.getPosting(postingId, controller.signal)])
      .then(([loadedApplicant, loadedPosting]) => {
        // An applicant reached under another posting's path is not this posting's applicant.
        if (loadedApplicant.jobPostingTagId !== loadedPosting.id) {
          setLoadError(new ApiError('That applicant does not belong to this job posting.', 404));
          return;
        }
        setApplicant(loadedApplicant);
        setPosting(loadedPosting);
        setLoadError(null);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setLoadError(caught instanceof ApiError ? caught : new ApiError('The applicant could not be loaded.', 0));
      });

    return () => controller.abort();
  }, [applicantId, postingId, reloadKey]);

  useEffect(() => {
    const controller = new AbortController();

    // A failure is the checklist card's to say, not the page's: the rest of the record still reads.
    applicantsApi
      .screening(applicantId, controller.signal)
      .then((loaded) => {
        setChecklist(loaded.checklist);
        setChecklistFailed(false);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setChecklistFailed(true);
      });

    return () => controller.abort();
  }, [applicantId, reloadKey]);

  useEffect(() => {
    const controller = new AbortController();

    // A failure is the stage tabs' and the Summary's to say; the rest of the record still reads.
    Promise.all([applicantsApi.assessments(applicantId, controller.signal), applicantsApi.scoreWeights(controller.signal)])
      .then(([loadedStages, loadedWeights]) => {
        setAssessments(loadedStages);
        setWeights(loadedWeights);
        setAssessmentsFailed(false);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setAssessmentsFailed(true);
      });

    return () => controller.abort();
  }, [applicantId, reloadKey]);

  /** A filed requirement, downloaded — the server serves it as an attachment, never inline. */
  async function downloadRequirementFile(line: ApplicantChecklistItem) {
    try {
      const { blob, fileName } = await applicantsApi.downloadRequirementFile(applicantId, line.requirementId);
      saveBlob(blob, fileName ?? line.attachmentFileName ?? 'requirement');
    } catch (caught) {
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not download the file.');
    }
  }

  const personIds = useMemo(() => (applicant ? [applicant.personId] : []), [applicant]);
  const names = usePersonNames(personIds, applicantsApi.names);
  const name = applicant ? applicantDisplayName(applicant.personId, names) : '';

  async function runConfirmed() {
    if (!applicant || !confirming) return;

    setRunning(true);
    setActionError(null);

    try {
      if (confirming === 'delete') {
        await applicantsApi.remove(applicant.id);
        toast.success('Applicant deleted.');
      } else {
        await applicantsApi.restore(applicant.id);
        toast.success('Applicant restored.');
      }
      setConfirming(null);
      reload();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-white">
      {/*
        The two loaded crumbs are APPENDED once known, never held as '' placeholders: the
        breadcrumb keys each crumb by its label, so two empty ones collide and React keeps a stale
        item — which drew a doubled separator.
      */}
      <Breadcrumb
        segments={[
          'Human Resources',
          'Recruitment',
          { label: 'Applicants', to: '/applicants' },
          ...(posting ? [{ label: posting.itemNumber, to: `/applicants/${posting.id}` }] : []),
          ...(applicant ? [name] : []),
        ]}
      />

      {loadError && !applicant ? (
        <ErrorState
          message={loadError.message}
          correlationId={loadError.correlationId}
          onRetry={loadError.isNotFound ? undefined : reload}
        />
      ) : !applicant || !posting ? (
        <ProfileHeaderSkeleton tabs={TABS.length} />
      ) : (
        <ProfileHeaderShell<TabKey>
          id="applicant-profile"
          tabs={TABS}
          activeTab={tab}
          onTabChange={setTab}
          // The Assessments tab draws its own rail and scroll region, edge to edge.
          panel={tab === 'assessments' ? 'bleed' : 'cards'}
          header={
            <ProfileHeader
              id="applicant-profile-header"
              icon={<UserRound className="h-5 w-5" />}
              title={name}
              meta={[
                posting.itemNumber,
                <span key="post" className="font-sans">
                  {posting.positionTitle}
                </span>,
              ]}
              status={
                applicant.isActive ? (
                  <StatusText tone={stageTone(applicant)}>{applicant.statusLabel}</StatusText>
                ) : (
                  <StatusText tone="inactive">Deleted</StatusText>
                )
              }
              createdBy={applicant.createdByName}
              createdAt={applicant.createdAt}
              settings={{
                onHistory: () => setHistoryOpen(true),
                // Every process of the cycle, always listed — a stage not open yet is DISABLED and says why.
                menuItems: processItems(applicant, {
                  screen: () => setScreening('checklist'),
                  interview: () => setInterviewing(true),
                  status: () => setMovingStatus(true),
                }),
                onDelete: () => setConfirming('delete'),
                deletePermission: 'applicants.delete',
                deleteDisabledReason: applicant.isActive ? null : 'This applicant is already deleted.',
              }}
            />
          }
          banner={
            applicant.isActive ? (
              <ProfileBanner
                id="applicant-profile-banner"
                message={bannerMessage(applicant)}
                action={
                  /*
                    The NEXT process for where the applicant stands — each opens a form, so the form's
                    own tick is the gate. Not screened → screen; Qualified with no interview → schedule;
                    scheduled → move the status on; Disqualified → re-screen (a correction).
                  */
                  <PermissionGate permission="applicants.update">
                    {applicant.screeningResult == null ||
                    applicant.screeningResult === APPLICANT_QUALIFICATION.Disqualified ? (
                      <Button
                        id="applicant-profile-screen"
                        variant="secondary"
                        leading={<ClipboardCheck className="h-4 w-4" aria-hidden="true" />}
                        onClick={() => setScreening('checklist')}
                      >
                        Initial screening
                      </Button>
                    ) : applicant.interviewDate == null ? (
                      <Button
                        id="applicant-profile-schedule-interview"
                        variant="secondary"
                        leading={<CalendarClock className="h-4 w-4" aria-hidden="true" />}
                        onClick={() => setInterviewing(true)}
                      >
                        Schedule interview
                      </Button>
                    ) : (
                      <Button
                        id="applicant-profile-change-status"
                        variant="secondary"
                        leading={<ListChecks className="h-4 w-4" aria-hidden="true" />}
                        onClick={() => setMovingStatus(true)}
                      >
                        Change status
                      </Button>
                    )}
                  </PermissionGate>
                }
              />
            ) : (
              <ProfileBanner
                id="applicant-profile-banner"
                message="This applicant is deleted. The record and the name are kept; restoring puts them back on the posting's applicants."
                action={
                  <PermissionGate permission="applicants.delete">
                    <Button
                      id="applicant-profile-restore"
                      variant="secondary"
                      leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                      onClick={() => setConfirming('restore')}
                    >
                      Restore
                    </Button>
                  </PermissionGate>
                }
              />
            )
          }
        >
          {tab === 'info' ? (
            <>
              <ProfileCard
                id="applicant-profile-applicant"
                title="Applicant"
                onEdit={() => setEditing(true)}
                editPermission="applicants.update"
                editDisabledReason={applicant.isActive ? null : 'This applicant is deleted. Restore them before editing.'}
              >
                <PreviewTable variant="table-display" rows={applicantRows(applicant, name)} />
              </ProfileCard>

              {/* Never editable: a record of what happened. */}
              <ProfileCard id="applicant-profile-audit" title="Audit">
                <PreviewTable variant="table-display" rows={auditRows(applicant)} />
              </ProfileCard>
            </>
          ) : tab === 'initial-screening' ? (
            /*
              The form's two STEPS, one card each and in its order — Recruitment checklist, then
              Screening. Each card's Edit opens the form on its own step; the write is the one
              screening request either way.
            */
            <>
              <ProfileCard
                id="applicant-profile-checklist"
                title="Recruitment checklist"
                count={checklist !== null ? checklist.length : undefined}
                onEdit={() => setScreening('checklist')}
                editPermission="applicants.update"
                editLabel="Initial screening"
                editDisabledReason={applicant.isActive ? null : 'This applicant is deleted. Restore them before screening.'}
                // The grid draws its OWN rounded border, so it sits inset.
                padded
                /*
                  The checklist is drawn BEFORE the screening too (product call, 26 Oct 2026) — the
                  reader is told what the applicant will be screened against, every line's Complied?
                  blank until answered. The Screening card below carries the "not screened" state.
                */
              >
                {checklist === null && !checklistFailed ? (
                  <Skeleton className="h-24 w-full" />
                ) : (
                  <PreviewGrid<ApplicantChecklistItem>
                    rows={checklist ?? []}
                    rowKey={(c) => c.requirementId}
                    empty={
                      checklistFailed
                        ? 'The checklist could not be loaded.'
                        : 'No requirements are set up yet. They are added under Recruitment › Settings › Applicant Requirements, or on the screening form.'
                    }
                    columns={[
                      {
                        header: 'Requirement',
                        width: 300,
                        render: (c) => <TextCell value={c.isActive ? c.name : `${c.name} (deleted from Settings)`} />,
                      },
                      {
                        header: 'Attachment',
                        width: 280,
                        render: (c) =>
                          c.attachmentFileName != null ? (
                            <RequirementFileLink
                              fileName={c.attachmentFileName}
                              onDownload={() => void downloadRequirementFile(c)}
                            />
                          ) : (
                            <TextCell value={null} />
                          ),
                      },
                      {
                        header: 'Complied?',
                        width: 140,
                        render: (c) => <TextCell value={c.isComplied == null ? null : c.isComplied ? 'Yes' : 'No'} />,
                      },
                    ]}
                  />
                )}
              </ProfileCard>

              <ProfileCard
                id="applicant-profile-screening"
                title="Screening"
                onEdit={() => setScreening(applicant.screeningResult == null ? 'checklist' : 'screening')}
                editPermission="applicants.update"
                editLabel="Initial screening"
                editDisabledReason={applicant.isActive ? null : 'This applicant is deleted. Restore them before screening.'}
                empty={
                  applicant.screeningResult == null
                    ? {
                        title: 'Not screened yet.',
                        description: 'The screening records who screened the applicant, when, and Qualified or Disqualified.',
                      }
                    : undefined
                }
              >
                <PreviewTable variant="table-display" rows={screeningRows(applicant)} />
              </ProfileCard>
            </>
          ) : tab === 'assessments' ? (
            /*
              ASSESSMENTS (product call, 21 Oct 2026) — the rail at the panel's left edge, the shape the
              Deliberate tabs give a panel (`<ProfileNav>` in a `w-56` aside); the open entry's cards on
              the right in their own scroll region, capped as a `cards` panel is.
            */
            <div className="flex h-full min-h-0 min-w-0">
              <aside
                aria-label="Assessments"
                className="modern-scrollbar w-56 shrink-0 overflow-y-auto overflow-x-hidden border-r border-slate-200 bg-white"
              >
                <ProfileNav
                  id="applicant-profile-assessments-nav"
                  entries={assessmentEntries(assessments)}
                  activeKey={assessmentPanel}
                  // The health stages HIDE without the medical key — the nav filters only when handed the check.
                  hasPermission={hasPermission}
                  onSelect={(key) => setAssessmentPanel(key as AssessmentPanelKey)}
                />
              </aside>

              <div className="modern-scrollbar min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-4">
                <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
                  {assessmentPanel === 'summary' ? (
                    <ApplicantSummaryPanel
                      assessments={assessments}
                      weights={weights}
                      failed={assessmentsFailed}
                      onWeightsSaved={setWeights}
                    />
                  ) : (
                    <AssessmentTab
                      applicant={applicant}
                      stageKey={assessmentPanel}
                      assessments={assessments}
                      failed={assessmentsFailed}
                      onEdit={setRecording}
                    />
                  )}
                </div>
              </div>
            </div>
          ) : (
            <ProfileCard
              id="applicant-profile-interview"
              title="Initial Interview"
              onEdit={() => setInterviewing(true)}
              editPermission="applicants.update"
              editLabel={applicant.interviewDate != null ? 'Reschedule' : 'Schedule interview'}
              editDisabledReason={
                !applicant.isActive
                  ? 'This applicant is deleted. Restore them before scheduling an interview.'
                  : applicant.screeningResult !== APPLICANT_QUALIFICATION.Qualified
                    ? 'Only an applicant screened Qualified is interviewed. Record the initial screening first.'
                    : null
              }
              empty={
                applicant.interviewDate == null
                  ? {
                      title: 'No interview scheduled.',
                      description:
                        applicant.screeningResult === APPLICANT_QUALIFICATION.Qualified
                          ? 'Set where and when the initial interview is held.'
                          : 'An interview is scheduled once the applicant is screened Qualified.',
                    }
                  : undefined
              }
            >
              <PreviewTable variant="table-display" rows={interviewRows(applicant)} />
            </ProfileCard>
          )}
        </ProfileHeaderShell>
      )}

      {applicant && editing ? (
        <ApplicantModal
          postingId={postingId}
          editing={applicant}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            reload();
          }}
        />
      ) : null}

      {applicant && movingStatus ? (
        <ApplicantStatusModal
          applicant={applicant}
          onClose={() => setMovingStatus(false)}
          onSaved={() => {
            setMovingStatus(false);
            reload();
          }}
        />
      ) : null}

      {applicant && screening ? (
        <ApplicantScreeningModal
          applicant={applicant}
          applicantName={name}
          initialStep={screening}
          onClose={() => setScreening(null)}
          onSaved={() => {
            setScreening(null);
            reload();
          }}
        />
      ) : null}

      {applicant && recording ? (
        <ApplicantAssessmentModal
          applicantId={applicant.id}
          applicantName={name}
          assessment={recording}
          onClose={() => setRecording(null)}
          onSaved={() => {
            setRecording(null);
            reload();
          }}
        />
      ) : null}

      {applicant && interviewing ? (
        <ApplicantInterviewModal
          applicant={applicant}
          applicantName={name}
          onClose={() => setInterviewing(false)}
          onSaved={() => {
            setInterviewing(false);
            reload();
          }}
        />
      ) : null}

      {applicant ? (
        <HistoryLogsModal
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          module="applicants"
          entityId={applicant.id}
        />
      ) : null}

      {applicant && confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setActionError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming === 'delete' ? 'Delete applicant' : 'Restore applicant'}
          danger={confirming === 'delete'}
          requireConfirm
          // The password on the destructive half only; the undo takes no extra safeguard.
          requireCredential={confirming === 'delete'}
          loading={running}
          id={`applicant-${confirming}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming === 'delete'
                  ? `${name} is taken off this posting's applicants. Nothing is erased — the record and the name are kept, and the applicant can be restored.`
                  : `${name} goes back on this posting's applicants at their last stage, ${applicant.statusLabel}.`}
              </p>
              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}

/**
 * One assessment stage's tab: its card, with the same disabled reasons the server's refusals give —
 * a deleted applicant, or one not screened Qualified. A failed load says so in place of the card.
 */
function AssessmentTab({
  applicant,
  stageKey,
  assessments,
  failed,
  onEdit,
}: {
  applicant: ApplicantListItem;
  stageKey: AssessmentTabKey;
  assessments: ApplicantAssessment[] | null;
  failed: boolean;
  onEdit: (assessment: ApplicantAssessment) => void;
}) {
  const stage = APPLICANT_ASSESSMENT_STAGES.find((s) => s.key === stageKey);

  if (!stage) return null;

  if (failed) {
    return (
      <ProfileCard id={`applicant-profile-assessment-${stage.value}`} title={stage.label} padded>
        <p className="text-sm text-slate-500">This stage could not be loaded. Refresh the page to try again.</p>
      </ProfileCard>
    );
  }

  const assessment = assessments?.find((a) => a.stage === stage.value) ?? null;

  return (
    <ApplicantAssessmentPanel
      applicantId={applicant.id}
      stageLabel={stage.label}
      assessment={assessment}
      editDisabledReason={
        !applicant.isActive
          ? 'This applicant is deleted. Restore them before recording an assessment.'
          : applicant.screeningResult !== APPLICANT_QUALIFICATION.Qualified
            ? 'Only an applicant screened Qualified is assessed. Record the initial screening first.'
            : null
      }
      qualified={applicant.screeningResult === APPLICANT_QUALIFICATION.Qualified}
      onEdit={() => {
        if (assessment) onEdit(assessment);
      }}
    />
  );
}

/** Selected is the good outcome; the two refusals are closed; every stage between is in progress. */
function stageTone(applicant: ApplicantListItem): StatusTone {
  if (applicant.status === APPLICANT_STATUS.Selected) return 'active';
  if (applicant.status === APPLICANT_STATUS.NotQualified || applicant.status === APPLICANT_STATUS.NotSelected) return 'inactive';
  return 'pending';
}


/** One fact per row; every row renders whatever the value, an absent one as an em dash. */
function applicantRows(applicant: ApplicantListItem, name: string): PreviewRow[] {
  return [
    { label: 'Name', value: name },
    { label: 'Email', value: applicant.email },
    { label: 'Mobile number', value: applicant.mobileNumber, mono: true },
    { label: 'Date applied', value: formatDateOnly(applicant.dateApplied), mono: true },
    { label: 'Remarks', value: applicant.remarks },
  ];
}

/**
 * The cycle's processes for the header cog, in cycle order. Gated on `applicants.update`; a process
 * the applicant's state does not allow yet is DISABLED with its reason, never hidden — the same
 * reasons the server's refusals give.
 */
function processItems(
  applicant: ApplicantListItem,
  open: { screen: () => void; interview: () => void; status: () => void },
): MenuItem[] {
  const deleted = applicant.isActive ? null : 'This applicant is deleted. Restore them first.';
  const notQualified =
    applicant.screeningResult === APPLICANT_QUALIFICATION.Qualified
      ? null
      : 'Only an applicant screened Qualified moves on. Record the initial screening first.';

  return [
    {
      key: 'initial-screening',
      label: 'Initial screening',
      icon: <ClipboardCheck className="h-4 w-4" aria-hidden="true" />,
      permission: 'applicants.update',
      disabledReason: deleted,
      onClick: open.screen,
    },
    {
      key: 'schedule-interview',
      label: applicant.interviewDate != null ? 'Reschedule interview' : 'Schedule interview',
      icon: <CalendarClock className="h-4 w-4" aria-hidden="true" />,
      permission: 'applicants.update',
      disabledReason: deleted ?? notQualified,
      onClick: open.interview,
    },
    {
      key: 'change-status',
      label: 'Change status',
      icon: <ListChecks className="h-4 w-4" aria-hidden="true" />,
      permission: 'applicants.update',
      disabledReason: deleted ?? notQualified,
      onClick: open.status,
    },
  ];
}

/** The banner's sentence: the stage, and — before the picker is open to them — why it is not. */
function bannerMessage(applicant: ApplicantListItem): string {
  if (applicant.screeningResult == null) {
    return `Application status: ${applicant.statusLabel}. Not screened yet — record the initial screening to move it on.`;
  }

  if (applicant.screeningResult === APPLICANT_QUALIFICATION.Disqualified) {
    return `Application status: ${applicant.statusLabel}, set by the initial screening.`;
  }

  return `Application status: ${applicant.statusLabel}.`;
}

/** The Screening step as recorded, in the form's own field order — the verdict last. */
function screeningRows(applicant: ApplicantListItem): PreviewRow[] {
  return [
    { label: 'Screened by', value: applicant.screenedByName },
    {
      label: 'Screened at',
      value: applicant.screenedDate != null ? formatDateOnly(applicant.screenedDate) : null,
      mono: true,
    },
    { label: 'Remarks', value: applicant.screeningRemarks },
    { label: 'Qualification', value: applicant.screeningResultLabel },
  ];
}

/**
 * A filed requirement's name, which DOWNLOADS it — the paperclip on its left. The full name rides
 * in the tooltip when it truncates.
 *
 * **IT IS `slate-500` AT REGULAR WEIGHT, because this is a TABLE CELL.** The root rule is explicit:
 * never the dark `slate-800`/`slate-900` in a table or calendar cell, and a clickable entry there
 * is told apart by HOVER rather than by tone (12 Oct 2026). "A clickable thing is black" is the
 * rule for PROSE, and it does not reach inside a grid — the two columns either side of this one
 * are `<TextCell>`s at `slate-500`, so a heavier tone here made the file name shout over the
 * requirement that names it.
 *
 * It shipped at `font-medium text-slate-800` under a comment claiming it matched "the form's own
 * Attachment cell". That was not true either: the screening modal's cell is `slate-600` regular,
 * the inline-grid tone. Read the sibling before copying it.
 */
function RequirementFileLink({ fileName, onDownload }: { fileName: string; onDownload: () => void }) {
  return (
    <Tooltip label={`Download ${fileName}`} block>
      <button
        type="button"
        onClick={onDownload}
        className="inline-flex min-w-0 max-w-full items-center gap-1 text-sm text-slate-500 hover:underline focus:outline-none focus-visible:underline"
      >
        <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="min-w-0 truncate">{fileName}</span>
        <span className="sr-only">, download</span>
      </button>
    </Tooltip>
  );
}

function interviewRows(applicant: ApplicantListItem): PreviewRow[] {
  return [
    { label: 'Place', value: applicant.interviewPlace },
    {
      label: 'Date',
      value: applicant.interviewDate != null ? formatDateOnly(applicant.interviewDate) : null,
      mono: true,
    },
    {
      label: 'Time',
      value: applicant.interviewTime != null ? formatClockTime(applicant.interviewTime) : null,
      mono: true,
    },
  ];
}

function auditRows(applicant: ApplicantListItem): PreviewRow[] {
  return [
    { label: 'Updated by', value: applicant.updatedByName },
    { label: 'Updated', value: applicant.updatedAt != null ? formatDateTime(applicant.updatedAt) : null, mono: true },
  ];
}
